import type { SalesListQuery } from '../../../api/endpoints';
import type { Dictionary } from '../../../i18n/en';
import type { InvoiceListSearch } from '../InvoiceListPage';
import { activeView, type ViewId } from '../list/views';

/**
 * The list criteria the batch page carries (WP-1b.3, BLOCK-1B-BULK-DESIGN §1 "The list entry", §3):
 * the search text, the facets, the buyer's name and the sort — never the page or the page size.
 */
export const CARRIED_KEYS = [
  'q',
  'status',
  'taxStatus',
  'from',
  'to',
  'customer',
  'customerName',
  'minAmount',
  'maxAmount',
  'sort',
  'dir',
] as const;
export type CarriedKey = (typeof CARRIED_KEYS)[number];
/** The carried criteria: the list's own search parameters, minus the page and the page size. */
export type BatchCriteria = Omit<InvoiceListSearch, 'tab' | 'page' | 'limit'>;

const SORTS: readonly string[] = ['number', 'date', 'customer', 'amount', 'status', 'updated'];

/** The carried criteria of a URL search (unknown values dropped; numbers read back as text). */
export function criteriaOf(search: Record<string, unknown>): BatchCriteria {
  const out: Record<string, string> = {};
  for (const key of CARRIED_KEYS) {
    const v = search[key];
    const text =
      typeof v === 'string' ? v : typeof v === 'number' && Number.isFinite(v) ? String(v) : '';
    if (!text) continue;
    if (key === 'sort' && !SORTS.includes(text)) continue;
    if (key === 'dir' && text !== 'asc' && text !== 'desc') continue;
    out[key] = text;
  }
  return out as BatchCriteria;
}

/** The URL search string of the criteria (`?q=…&status=…`), or '' when there are none. */
export function criteriaSearch(criteria: BatchCriteria): string {
  const params = new URLSearchParams();
  for (const key of CARRIED_KEYS) {
    const v = criteria[key];
    if (v) params.set(key, v);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** The batch page's address with the list's criteria (the list entry's href). */
export const batchHref = (orgId: string, search: InvoiceListSearch) =>
  `/orgs/${encodeURIComponent(orgId)}/sales/invoices/batch${criteriaSearch(
    criteriaOf(search as Record<string, unknown>),
  )}`;

/** The billing list with the same criteria (the eyebrow link and «غيّر المعايير في القائمة»). */
export const listHref = (orgId: string, criteria: BatchCriteria) =>
  `/orgs/${encodeURIComponent(orgId)}/sales/invoices${criteriaSearch(criteria)}`;

/** The list API query of the criteria (the list's own mapping; `customer` → `receiverPartyId`). */
export function listQueryOf(criteria: BatchCriteria): SalesListQuery {
  return {
    tab: 'invoices',
    ...(criteria.q ? { q: criteria.q } : {}),
    ...(criteria.status ? { status: criteria.status } : {}),
    ...(criteria.taxStatus ? { taxStatus: criteria.taxStatus } : {}),
    ...(criteria.from ? { from: criteria.from } : {}),
    ...(criteria.to ? { to: criteria.to } : {}),
    ...(criteria.customer ? { receiverPartyId: criteria.customer } : {}),
    ...(criteria.minAmount ? { minAmount: criteria.minAmount } : {}),
    ...(criteria.maxAmount ? { maxAmount: criteria.maxAmount } : {}),
    sort: criteria.sort ?? 'updated',
    dir: criteria.dir === 'asc' ? 'asc' : 'desc',
  };
}

function viewName(t: Dictionary, id: ViewId): string {
  switch (id) {
    case 'all':
      return t.sales.viewAll;
    case 'drafts':
      return t.sales.kpis.drafts;
    case 'sending':
      return t.sales.kpis.sending;
    case 'attention':
      return t.sales.kpis.attention;
    case 'valid':
      return t.sales.kpis.valid;
    case 'deleted':
      return t.sales.viewDeleted;
  }
}

/**
 * «المعايير: …» (§3): the active preset view's name when the facets are exactly a view's (the
 * "Need attention" view needs both its status and its tax status); else each filled facet with its
 * existing label, joined with « · »; else «كل المستندات». A search text is always named.
 */
export function criteriaLabel(t: Dictionary, criteria: BatchCriteria): string {
  const view = activeView(criteria);
  const parts: string[] = [];
  if (view && view !== 'all') parts.push(viewName(t, view));
  else if (!view) {
    const list = (v: string | undefined, label: (x: string) => string) =>
      (v ?? '').split(',').filter(Boolean).map(label).join('، ');
    if (criteria.status)
      parts.push(
        `${t.sales.filterStatus}: ${list(criteria.status, (x) => (t.docStatus as Record<string, string>)[x] ?? x)}`,
      );
    if (criteria.taxStatus)
      parts.push(
        `${t.sales.filterTaxStatus}: ${list(criteria.taxStatus, (x) => (t.taxStatus as Record<string, string>)[x] ?? x)}`,
      );
    if (criteria.from) parts.push(`${t.sales.filterFrom}: ${criteria.from}`);
    if (criteria.to) parts.push(`${t.sales.filterTo}: ${criteria.to}`);
    if (criteria.customer)
      parts.push(`${t.sales.filterCustomer}: ${criteria.customerName ?? criteria.customer}`);
    if (criteria.minAmount) parts.push(`${t.sales.filterMin}: ${criteria.minAmount}`);
    if (criteria.maxAmount) parts.push(`${t.sales.filterMax}: ${criteria.maxAmount}`);
  }
  if (criteria.q) parts.push(`${t.sales.search}: ${criteria.q}`);
  return parts.length ? parts.join(' · ') : t.sales.viewAll;
}
